import moment from "moment-timezone";
import LotteryV2StepType from "@tootsweet/model/lottery/LotteryV2StepType";
import type ClientSideLotteryV2Step from "@tootsweet/model/lottery/ClientSideLotteryV2Step";
import { SECTOR_NAME } from "@tootsweet/model/enums/SectorName";
import {
  faCamera,
  faCameraRetro,
  faEnvelope,
  faG,
  faGhost,
  faMobile,
  faThumbsUp,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import { tt } from "./i18n/i18n";
import TSUtils from "./helpers/TSUtils";
import DEFAULT_EVENT from "../model/DEFAULT_EVENT";
import { ActiveEvent, EventConfig, GameTypes } from "kadow-jetpack-model";
import OnboardingAPI from "../app/03_Onboarding/helpers/OnboardingAPI";
import CONFIG from "../model/TSConfig";

export const BASE_URL = `${window.location.origin}${CONFIG.baseName ? `/${CONFIG.baseName}` : ""}`;

export const KADOW_COLOR_POSITIVE = "#16A34A";
export const KADOW_COLOR_NEGATIVE = "#DC2626";

export const STORAGE_TOKEN_KEY = "ngStorage-ts_auth_token";
export const STORAGE_LAST_MANAGED_ACCOUNT_KEY =
  "ngStorage-ts_last_managed_account";
export const STORAGE_SIGNUP_FORM_VALUES_KEY = "ngStorage-ts_signup_form_values";
export const HEADER_TOKEN = "X-TS-pro-Token";
export const COUNTRIES = ["fr", "mq", "gf", "tf", "pf"];
export const SECONDS_IN_A_DAY = 86400;
export const MONTHLY_LOTTERY_MONTH_AND_YEAR_FORMAT = "YYYY-MM";
export const DEFAULT_LINE_DRIFT = 0.2;

export const DEFAULT_STALE_CACHE_TIME = 30 * 60 * 1000;

export const FIELD_REQUIRED = tt("field_required");

export const MUST_BE_NUMBER_FOR_TOKEN = tt("must_be_number_for_token");

export const PERCENTAGE = tt("percentage_validation");

export const MILLISECOND_IN_A_SECOND = 1000;

export const COLLECT_TVA_COUNTRY = "FR";
export const DEFAULT_PLAN_OPTION = "yearly";
export const DURATION_BASED_DISCOUNT = 0.4;

export const DEFAULT_URL_FOR_QRCODE = `https://kadow.club/les-jeux-kadow-club?utm_medium=qrcode&utm_source=social_networks`;

export const QRCODE_OPTIONS = {
  color: {
    dark: "#000"
  },
  width: 2048,
  margin: 0
};

export const reminderDelays = [
  {
    value: 0,
    name: tt("reminder_disabled")
  },
  {
    value: 12 * 3600,
    name: tt("reminder_12h")
  },
  {
    value: 24 * 3600,
    name: tt("reminder_24h")
  },
  {
    value: 86400 * 3,
    name: tt("reminder_d_plus_3")
  },
  {
    value: 86400 * 7,
    name: tt("reminder_w_plus_1")
  },
  {
    value: 86400 * 14,
    name: tt("reminder_w_plus_2")
  },
  {
    value: 86400 * 21,
    name: tt("reminder_w_plus_3")
  },
  {
    value: 86400 * 30,
    name: tt("reminder_m_plus_1")
  },
  {
    value: 86400 * 2 * 30,
    name: tt("reminder_m_plus_2")
  },
  {
    value: 86400 * 3 * 30,
    name: tt("reminder_m_plus_3")
  },
  {
    value: 86400 * 4 * 30,
    name: tt("reminder_m_plus_4")
  },
  {
    value: 86400 * 5 * 30,
    name: tt("reminder_m_plus_5")
  },
  {
    value: 86400 * 6 * 30,
    name: tt("reminder_m_plus_6")
  },
  {
    value: 86400 * 12 * 30,
    name: tt("reminder_m_plus_12")
  }
];

export const IG_STORIES = "IGStory";

export const STEP_TYPES: ClientSideLotteryV2Step[] = [
  {
    value: LotteryV2StepType.Google,
    name: tt("step_google_reviews"),
    img: faG
  },
  {
    value: LotteryV2StepType.Instagram,
    name: tt("step_instagram_follow"),
    img: faCamera
  },
  {
    value: LotteryV2StepType.Snapchat,
    name: tt("step_snapchat_follow"),
    img: faGhost
  },
  {
    value: IG_STORIES,
    name: tt("step_ig_story"),
    img: faCamera
  },
  {
    value: LotteryV2StepType.Email,
    name: tt("step_email_collect"),
    img: faEnvelope
  },
  {
    value: LotteryV2StepType.Phone,
    name: tt("step_phone_collect"),
    img: faMobile
  },
  {
    value: LotteryV2StepType.Facebook,
    name: tt("step_facebook_like"),
    img: faThumbsUp
  },
  {
    // This icon got remove in latest version of Fontawesome
    value: LotteryV2StepType.TripAdvisor,
    name: tt("step_tripadvisor_review"),
    customIcon: true,
    img: '<i class="fa fa-tripadvisor"></i>'
  },
  {
    value: LotteryV2StepType.Sponsorship,
    name: tt("step_sponsorship_action"),
    img: faUser
  },
  {
    value: LotteryV2StepType.SocialPhoto,
    name: tt("step_social_photo_action"),
    img: faCameraRetro
  }
];

export interface PeriodShortcut {
  key: number;
  name: string;
  dates: () => { startDate: Date; endDate: Date };
}

export const PERIOD_SHORTCUTS: PeriodShortcut[] = [
  {
    key: -1,
    name: tt("period_shortcut_1_day"),
    dates: () => {
      const endDate = moment().endOf("day");
      const startDate = moment().startOf("day").toDate();
      return { startDate, endDate: endDate.toDate() };
    }
  },
  {
    key: 0,
    name: tt("period_shortcut_2_weeks"),
    dates: () => {
      const endDate = moment().endOf("isoWeek").endOf("day");
      const startDate = moment()
        .startOf("isoWeek")
        .startOf("day")
        .subtract(1, "week")
        .toDate();
      return { startDate, endDate: endDate.toDate() };
    }
  },
  {
    key: 1,
    name: tt("period_shortcut_1_month"),
    dates: () => {
      const endDate = moment().endOf("isoWeek").endOf("day");
      const startDate = moment()
        .startOf("isoWeek")
        .startOf("day")
        .subtract(1, "month")
        .add(1, "week")
        .toDate();
      return { startDate, endDate: endDate.toDate() };
    }
  },
  {
    key: 2,
    name: tt("period_shortcut_3_months"),
    dates: () => {
      const endDate = moment().endOf("isoWeek").endOf("day");
      const startDate = moment()
        .startOf("isoWeek")
        .startOf("day")
        .subtract(3, "month")
        .add(1, "week")
        .toDate();
      return { startDate, endDate: endDate.toDate() };
    }
  },
  {
    key: 3,
    name: tt("period_shortcut_6_months"),
    dates: () => {
      const endDate = moment().endOf("isoWeek").endOf("day");
      const startDate = moment()
        .startOf("isoWeek")
        .startOf("day")
        .subtract(6, "month")
        .add(1, "week")
        .toDate();
      return { startDate, endDate: endDate.toDate() };
    }
  }
];

export const DEFAULT_PC_GIFT = 33;

export const DEFAULT_PRIZE_EXPIRY = {
  method: "relative",
  value: SECONDS_IN_A_DAY * 14
} as const;

export interface ISector {
  prizes: string[];
  name: string;
  icon: string[];
  value: SECTOR_NAME;
}

export const SECTOR_LIST: ISector[] = [
  {
    value: SECTOR_NAME.BAR,
    name: tt("jp_sector_bar"),
    prizes: [
      tt("jp_prize_sector_bar_00"),
      tt("jp_prize_sector_bar_01"),
      tt("jp_prize_sector_bar_02")
    ],
    icon: ["🍟", "🧀", "🍸"]
  },
  {
    value: SECTOR_NAME.CAFE,
    name: tt("jp_sector_cafe"),
    prizes: [
      tt("jp_prize_sector_cafe_00"),
      tt("jp_prize_sector_bar_01"),
      tt("jp_prize_sector_bar_02")
    ],
    icon: ["☕️", "🧀", "🍸"]
  },
  {
    value: SECTOR_NAME.RESTAURANT,
    name: tt("jp_sector_restaurant"),
    prizes: [
      tt("jp_prize_sector_restaurant_00"),
      tt("jp_prize_sector_restaurant_01"),
      tt("jp_prize_sector_restaurant_02")
    ],
    icon: ["🍰", "🍹", "🍽️"]
  },
  {
    value: SECTOR_NAME.HOTEL,
    name: tt("jp_sector_hotel"),
    prizes: [
      tt("jp_prize_sector_hotel_00"),
      tt("jp_prize_sector_hotel_01"),
      tt("jp_prize_sector_hotel_02")
    ],
    icon: ["☕️", "🥂", "🏷️"]
  },
  {
    value: SECTOR_NAME.SHOP,
    name: tt("jp_sector_shop"),
    prizes: [
      tt("jp_prize_sector_shop_00"),
      tt("jp_prize_sector_shop_01"),
      tt("jp_prize_sector_shop_02")
    ],
    icon: ["🏷️", "👜", "👑"]
  },
  {
    value: SECTOR_NAME.PARK,
    name: tt("jp_sector_park"),
    prizes: [
      tt("jp_prize_sector_park_00"),
      tt("jp_prize_sector_park_01"),
      tt("jp_prize_sector_park_02")
    ],
    icon: ["🎟️", "🍹", "🎁"]
  },
  {
    value: SECTOR_NAME.WELLNESS,
    name: tt("wellness"),
    prizes: [
      tt("jp_prize_wellness_00"),
      tt("jp_prize_wellness_01"),
      tt("jp_prize_wellness_02")
    ],
    icon: ["💆‍♂️", "🪮", "🏷️"]
  },
  {
    value: SECTOR_NAME.OTHER,
    name: tt("other"),
    prizes: [
      tt("jp_prize_sector_park_00"),
      tt("jp_prize_sector_park_01"),
      tt("jp_prize_sector_park_02")
    ],
    icon: ["🎟️", "🍹", "🎁"]
  }
];

export const REQUIRED_INFO_SECTOR = ["restaurant", "cafe"];

export const CLIENTS_PER_DAY_ANSWER = [
  {
    name: "less_than_50",
    displayName: tt("clients_per_day_less_than_50")
  },
  {
    name: "50_to_100",
    displayName: tt("clients_per_day_50_to_100")
  },
  {
    name: "100_to_200",
    displayName: tt("clients_per_day_100_to_200")
  },
  {
    name: "more_than_200",
    displayName: tt("clients_per_day_more_than_200")
  },
  {
    name: "unknown",
    displayName: tt("clients_per_day_unknown")
  }
];

export const PARK_POSA_ITEM = [
  {
    id: "establishment",
    name: tt("posa_category_establishment"),
    displayName: tt("posa_category_establishment_display"),
    items: [
      {
        name: tt("posa_item_a4_display"),
        displayName: tt("posa_item_a4_display_display")
      },
      {
        name: tt("posa_item_shelf_stopper"),
        displayName: tt("posa_item_shelf_stopper_display")
      },
      {
        name: tt("posa_item_window_sticker"),
        displayName: tt("posa_item_window_sticker_display")
      },
      {
        name: tt("posa_item_kakemono"),
        displayName: tt("posa_item_kakemono_display")
      },
      {
        name: tt("posa_item_poster"),
        displayName: tt("posa_item_poster_display")
      }
    ]
  },
  {
    id: "hand-to-hand",
    name: tt("posa_category_hand_to_hand"),
    displayName: tt("posa_category_hand_to_hand_display"),
    items: [
      {
        name: tt("posa_item_a6_flyer"),
        displayName: tt("posa_item_a6_flyer_display"),
        recommended: true
      },
      {
        name: tt("posa_item_business_card"),
        displayName: tt("posa_item_business_card_display")
      },
      {
        name: tt("posa_item_gift_card"),
        displayName: tt("posa_item_gift_card_display")
      }
    ]
  },
  {
    id: "social-media",
    name: tt("posa_category_social_media"),
    displayName: tt("posa_category_social_media_display"),
    items: [
      {
        name: tt("posa_item_story_format"),
        displayName: tt("posa_item_story_format_display")
      },
      {
        name: tt("posa_item_post_format"),
        displayName: tt("posa_item_post_format_display")
      }
    ]
  }
];

export const SHOP_POSA_ITEM = [
  {
    id: "point-of-sale",
    name: tt("posa_category_point_of_sale"),
    displayName: tt("posa_category_point_of_sale_display"),
    items: [
      {
        name: tt("posa_item_a4_display"),
        displayName: tt("posa_item_a4_display_display")
      },
      {
        name: tt("posa_item_shelf_stopper"),
        displayName: tt("posa_item_shelf_stopper_display")
      },
      {
        name: tt("posa_item_window_sticker"),
        displayName: tt("posa_item_window_sticker_display")
      },
      {
        name: tt("posa_item_kakemono"),
        displayName: tt("posa_item_kakemono_display")
      },
      {
        name: tt("posa_item_poster"),
        displayName: tt("posa_item_poster_display")
      }
    ]
  },
  {
    id: "hand-to-hand",
    name: tt("posa_category_hand_to_hand"),
    displayName: tt("posa_category_hand_to_hand_display"),
    items: [
      {
        name: tt("posa_item_a6_flyer"),
        displayName: tt("posa_item_a6_flyer_display"),
        recommended: true
      },
      {
        name: tt("posa_item_business_card"),
        displayName: tt("posa_item_business_card_display")
      },
      {
        name: tt("posa_item_gift_card"),
        displayName: tt("posa_item_gift_card_display")
      }
    ]
  },
  {
    id: "delivery-bags",
    name: tt("posa_category_delivery_bags"),
    displayName: tt("posa_category_delivery_bags_display"),
    items: [
      {
        name: tt("posa_item_stapled_flyer"),
        displayName: tt("posa_item_stapled_flyer_display")
      },
      {
        name: tt("posa_item_bag_printing"),
        displayName: tt("posa_item_bag_printing_display"),
        recommended: true
      }
    ]
  },
  {
    id: "social-media",
    name: tt("posa_category_social_media"),
    displayName: tt("posa_category_social_media_display"),
    items: [
      {
        name: tt("posa_item_story_format"),
        displayName: tt("posa_item_story_format_display")
      },
      {
        name: tt("posa_item_post_format"),
        displayName: tt("posa_item_post_format_display")
      }
    ]
  }
];

export const HOTEL_POSA_ITEM = [
  {
    id: "rooms",
    name: tt("posa_category_rooms"),
    displayName: tt("posa_category_rooms_display"),
    items: [
      {
        name: tt("posa_item_paper_menu"),
        displayName: tt("posa_item_paper_menu_display")
      },
      {
        name: tt("posa_item_welcome_booklet"),
        displayName: tt("posa_item_welcome_booklet_display")
      },
      {
        name: tt("posa_item_sticker"),
        displayName: tt("posa_item_sticker_display")
      },
      {
        name: tt("posa_item_other_printable"),
        displayName: tt("posa_item_other_printable_display")
      }
    ]
  },
  {
    id: "reception-common",
    name: tt("posa_category_reception_common"),
    displayName: tt("posa_category_reception_common_display"),
    items: [
      {
        name: tt("posa_item_a4_display"),
        displayName: tt("posa_item_a4_display_display")
      },
      {
        name: tt("posa_item_kakemono"),
        displayName: tt("posa_item_kakemono_display")
      },
      {
        name: tt("posa_item_poster"),
        displayName: tt("posa_item_poster_display")
      },
      {
        name: tt("posa_item_sticker"),
        displayName: tt("posa_item_sticker_display")
      }
    ]
  },
  {
    id: "table",
    name: tt("posa_category_table"),
    displayName: tt("posa_category_table_display"),
    items: [
      {
        name: tt("posa_item_dome_sticker"),
        displayName: tt("posa_item_dome_sticker_display"),
        recommended: true
      },
      {
        name: tt("posa_item_a6_display"),
        displayName: tt("posa_item_a6_display_display")
      },
      {
        name: tt("posa_item_coasters"),
        displayName: tt("posa_item_coasters_display")
      }
    ]
  },
  {
    id: "hand-to-hand",
    name: tt("posa_category_hand_to_hand"),
    displayName: tt("posa_category_hand_to_hand_display"),
    items: [
      {
        name: tt("posa_item_a6_flyer"),
        displayName: tt("posa_item_a6_flyer_display"),
        recommended: true
      },
      {
        name: tt("posa_item_business_card"),
        displayName: tt("posa_item_business_card_display")
      },
      {
        name: tt("posa_item_gift_card"),
        displayName: tt("posa_item_gift_card_display")
      }
    ]
  },
  {
    id: "social-media",
    name: tt("posa_category_social_media"),
    displayName: tt("posa_category_social_media_display"),
    items: [
      {
        name: tt("posa_item_story_format"),
        displayName: tt("posa_item_story_format_display")
      },
      {
        name: tt("posa_item_post_format"),
        displayName: tt("posa_item_post_format_display")
      }
    ]
  }
];

export const POSA_Items = [
  {
    id: "table",
    name: tt("posa_category_at_table"),
    displayName: tt("posa_category_at_table_display"),
    items: [
      {
        name: tt("posa_item_dome_sticker"),
        displayName: tt("posa_item_dome_sticker_display"),
        recommended: true
      },
      {
        name: tt("posa_item_a6_table_tent"),
        displayName: tt("posa_item_a6_table_tent_display")
      }
    ]
  },
  {
    id: "counter",
    name: tt("posa_category_at_counter"),
    displayName: tt("posa_category_at_counter_display"),
    items: [
      {
        name: tt("posa_item_a4_counter_display"),
        displayName: tt("posa_item_a4_counter_display_display")
      },
      {
        name: tt("posa_item_shelf_stopper"),
        displayName: tt("posa_item_shelf_stopper_display")
      }
    ]
  },
  {
    id: "hand-to-hand",
    name: tt("posa_category_hand_to_hand"),
    displayName: tt("posa_category_hand_to_hand_display"),
    items: [
      {
        name: tt("posa_item_a6_flyer"),
        displayName: tt("posa_item_a6_flyer_display")
      },
      {
        name: tt("posa_item_business_card"),
        displayName: tt("posa_item_business_card_display")
      },
      {
        name: tt("posa_item_gift_card"),
        displayName: tt("posa_item_gift_card_display")
      }
    ]
  },
  {
    id: "reprint-existing",
    name: tt("posa_category_reprint_existing"),
    displayName: tt("posa_category_reprint_existing_display"),
    items: [
      {
        name: tt("posa_item_placemat"),
        displayName: tt("posa_item_placemat_display"),
        recommended: true
      },
      {
        name: tt("posa_item_paper_menu"),
        displayName: tt("posa_item_paper_menu_display")
      }
    ]
  },
  {
    id: "delivery-bags",
    name: tt("posa_category_delivery_bags"),
    displayName: tt("posa_category_delivery_bags_display"),
    items: [
      {
        name: tt("posa_item_stapled_a6_flyer"),
        displayName: tt("posa_item_stapled_a6_flyer_display")
      },
      {
        name: tt("posa_item_bag_printing"),
        displayName: tt("posa_item_bag_printing_display")
      }
    ]
  },
  {
    id: "social-media",
    name: tt("posa_category_social_media"),
    displayName: tt("posa_category_social_media_display"),
    items: [
      {
        name: tt("posa_item_story_format"),
        displayName: tt("posa_item_story_format_display")
      },
      {
        name: tt("posa_item_post_format"),
        displayName: tt("posa_item_post_format_display")
      }
    ]
  }
];

// Base default events list. We mutate this array once the neutre event has been fetched.
export const DEFAULT_EVENTS: EventConfig[] = [DEFAULT_EVENT];

export const NEUTRAL_EVENT_ID = "neutre_6864fa1b85e5f827c020b8c6";
export const DARK_MODE_ALL_EXCEPT_WHEEL_EVENT_ID =
  "dark-mode---tous-sauf-ruoe_697919d43ab6c1becd0023cc";
export const DARK_MODE_EVENT_ID = "dark-mode_683ff6553d288023c6baec7d";

/**
 * Fetches the neutre event and pushes it into DEFAULT_EVENTS in-memory list.
 * Call this once the user is authenticated so that a valid token is available.
 */
export const fetchNeutreEventAndUpdateDefaults = async (): Promise<void> => {
  try {
    const neutreEvent = await OnboardingAPI.getEventById(NEUTRAL_EVENT_ID);

    if (
      neutreEvent &&
      !DEFAULT_EVENTS.some((ev) => ev._id === neutreEvent._id)
    ) {
      DEFAULT_EVENTS.push(neutreEvent);
    }
  } catch (error) {
    console.error(tt("error_failed_fetch_neutre_event"), error);
  }
};

export const HTTP_404 = 404;

export const DEFAULT_TIMEZONE = "Europe/Paris";
export const DEFAULT_COUNTRY_CODE = "US";

const ISO_MONTH = {
  jan: 0,
  fev: 1,
  mar: 2,
  avr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11
};

export const END_OF_MONTH_DAY = 0;

function buildSuggestedEvent(
  id: string,
  gameType: GameTypes,
  startDay: number,
  startMonth: number,
  endDay: number,
  endMonth: number
): ActiveEvent {
  const sts = TSUtils.getNextDate(startDay, startMonth, true);
  const ets =
    endDay === END_OF_MONTH_DAY
      ? TSUtils.getNextDate(1, endMonth, false, sts)
      : TSUtils.getNextDate(endDay, endMonth, false, sts);
  return { id, gameType, sts, ets };
}

export const SUGGESTED_EVENTS_LIST: ActiveEvent[] = [
  buildSuggestedEvent(
    "noel_682c4b096483155ced90398c",
    GameTypes.KadowCrush,
    1,
    ISO_MONTH.dec,
    31,
    ISO_MONTH.dec
  ),
  buildSuggestedEvent(
    "saint-valentin_68595786378354f1aff3c3d3",
    GameTypes.Catcher,
    1,
    ISO_MONTH.fev,
    END_OF_MONTH_DAY,
    ISO_MONTH.fev
  ),
  buildSuggestedEvent(
    "halloween_685bc73e3556565da08747c6",
    GameTypes.Puzzle,
    1,
    ISO_MONTH.oct,
    31,
    ISO_MONTH.oct
  ),
  buildSuggestedEvent(
    "paques_685bc6703556565da08747c5",
    GameTypes.FortuneWheel,
    1,
    ISO_MONTH.avr,
    30,
    ISO_MONTH.avr
  )
];

export const TIME_ZONES: readonly string[] = moment.tz.names();

export const DEFAULT_GAME_COLORS = {
  background: "#ffffff",
  primary: "",
  titles: "#503e3e",
  accent: "#ff0054",
  subtitle: "#d7b146"
};

export const DEFAULT_POSA_COLORS = {
  primary: DEFAULT_GAME_COLORS.accent,
  accent: "#000000",
  background: DEFAULT_GAME_COLORS.background,
  titles: "",
  subtitle: DEFAULT_GAME_COLORS.subtitle
};

export const POSA_WORDING_KEY_DEFAULT = "cadeau";

export const POSA_CDN_PREFIX = "saas_posa_v6";
export const COLOR_PICKER_WIDTH = 250;
export const COLOR_PICKER_MODEL = "hex";

export const FLYERS_ORDER_STEP_QUANTITY = "quantity";
export const FLYERS_ORDER_STEP_DELIVERY = "delivery";
export const FLYERS_ORDER_STEP_PAYMENT = "payment";
